import {gql} from '@apollo/client';

import {
  ExportDataCatalogDatasetVariableName,
  SendToDocumentVariableName,
} from 'analysis-tool/AnalysisStepConfigVariableNames';
import {AnalysisTemplateGraphQL} from 'analysis-tool/templates/AnalysisTemplateTypes';
import {isFileConfigVariable} from 'analysis-tool/templates/AnalysisTemplateVariableUtils';
import {ObjectType} from 'app-core/framework/CoreObjectConstants';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {PipelineConfigJsonSpec} from 'pipelines/StepShapeUtils';
import {FieldType} from 'schemas/fields/SchemaFieldConstants';

export const GET_ANALYSIS_TEMPLATE_FOR_ASSAY_RUN_QUERY = gql`
  query GetAnalysisTemplateForAssayRun($analysisTemplateId: StrictString!)
  @skipAudit
  @engComponent(name: PIPELINES) {
    analysisTemplate(id: $analysisTemplateId) {
      ... on AnalysisTemplateNode {
        __typename
        id
        name
        configVariables {
          ... on AnalysisTemplateVariableNode {
            __typename
            id
            name
            variableTypeConfigSpec
          }
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export const GET_ASSAY_RUN_ANALYSIS_IDS = gql`
  query GetAssayRunAnalysisIds($runId: StrictString!) @skipAudit @engComponent(name: PIPELINES) {
    run(id: $runId) {
      ... on RunNode {
        __typename
        id
        analysisIds
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export const GET_ANALYSES_FOR_ASSAY_RUN_QUERY = gql`
  query GetAnalysesForAssayRun($analysisIds: [StrictString!]!) @skipAudit @engComponent(name: PIPELINES) {
    analyses(ids: $analysisIds) {
      ... on AnalysisNode {
        __typename
        id
        name
        createdAt
        statusCode
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

// Keep in sync with benchling/results/analysis_template_integration_utils.py
export function convertApiIdToSchemaFieldSystemName(apiIdentifier: string): string {
  const underscoreIndex = apiIdentifier.indexOf('_');
  const prefix = apiIdentifier.substring(0, underscoreIndex);
  let suffix = '_';

  for (let i = underscoreIndex + 1; i < apiIdentifier.length; i++) {
    const char = apiIdentifier[i];
    if (char === char.toUpperCase() && char !== char.toLowerCase()) {
      suffix += `${char.toLowerCase()}_`;
    } else {
      suffix += char;
    }
  }

  return prefix + suffix;
}

export interface AnalysisTemplateVariableSchemaFieldSpec {
  name: string;
  systemName: string;
  fieldType: FieldType;
  isMulti: boolean;
  isRequired: boolean;
  variableId: string; // The ID of the analysis config variable
}

/**
 * Maps domain types to corresponding field types
 */
const DOMAIN_TYPE_TO_FIELD_TYPE_MAP: Partial<Record<ObjectType, FieldType>> = {
  AA_SEQUENCE: FieldType.BIOENTITY_LINK,
  CUSTOM_ENTITY: FieldType.BIOENTITY_LINK,
  DNA_SEQUENCE: FieldType.BIOENTITY_LINK,
  MIXTURE: FieldType.BIOENTITY_LINK,
  MOLECULE: FieldType.BIOENTITY_LINK,
  PLATE: FieldType.STORABLE_LINK,
  BOX: FieldType.STORABLE_LINK,
  CONTAINER: FieldType.STORABLE_LINK,
};

export function isVariableMulti(variableTypeConfigSpec: PipelineConfigJsonSpec): boolean {
  return variableTypeConfigSpec.kind === 'array';
}

/**
 * Converts an analysis config variable specification to a field type and isMulti flag.
 * Returns null if the variable type cannot be mapped to a schema field.
 */
export function getSchemaFieldTypeAndMultiFromVariableSpec(variableTypeConfigSpec: PipelineConfigJsonSpec): {
  fieldType: FieldType;
  isMulti: boolean;
} | null {
  // Get the scalar config spec - for arrays, use elementSpec; otherwise use the spec itself
  const scalarConfigSpec =
    variableTypeConfigSpec.kind === 'array' ? variableTypeConfigSpec.elementSpec : variableTypeConfigSpec;

  let isMulti = false;
  let fieldType: FieldType;

  switch (scalarConfigSpec.kind) {
    case 'boolean':
      fieldType = FieldType.BOOLEAN;
      break;
    case 'integer':
      fieldType = FieldType.INTEGER;
      break;
    case 'number':
      fieldType = FieldType.FLOAT;
      break;
    case 'string':
      fieldType = FieldType.TEXT;
      break;
    case 'domain': {
      const domainType = scalarConfigSpec.types[0];
      const mappedFieldType = domainType != null ? DOMAIN_TYPE_TO_FIELD_TYPE_MAP[domainType] : null;
      if (mappedFieldType == null) {
        return null;
      }
      fieldType = mappedFieldType;
      isMulti = variableTypeConfigSpec.kind === 'array';
      break;
    }
    default:
      return null;
  }

  return {
    fieldType,
    isMulti,
  };
}

/**
 * Config variable keys that should not be mapped to schema fields
 * Keep in sync with `benchling/analysis_tool/domain/analysis_input_types.py::CreateAnalysisFromAssayRun._get_implicit_bindings`
 */
const IMPLICIT_VARIABLE_KEYS: Set<string> = new Set([
  SendToDocumentVariableName.DOCUMENT_ID,
  SendToDocumentVariableName.SECTION_INDEX,
  ExportDataCatalogDatasetVariableName.DATASET_NAME,
  ExportDataCatalogDatasetVariableName.FOLDER_ID,
]);

/**
 * Filters config variables to return only explicit ones (excluding implicit variable keys).
 */
export function getExplicitConfigVariables<T extends {configVariableKey: string}>(
  configVariables: Array<T>
): Array<T> {
  return configVariables.filter((variable) => !IMPLICIT_VARIABLE_KEYS.has(variable.configVariableKey));
}

/**
 * Generates run field specifications from analysis template config variables.
 *
 * @param analysisTemplate - The analysis template containing config variables
 * @returns Success with array of schema field specifications, or error with message
 */
export function generateRunFieldsSpecFromAnalysisTemplateVariables(
  analysisTemplate: AnalysisTemplateGraphQL
): {success: true; fields: Array<AnalysisTemplateVariableSchemaFieldSpec>} | {success: false; error: string} {
  const fields: Array<AnalysisTemplateVariableSchemaFieldSpec> = [];

  // Get explicit variables (exclude implicit bindings and files which are handled separately)
  const explicitVariables = getExplicitConfigVariables(analysisTemplate.configVariables).filter(
    (variable) => !isFileConfigVariable(variable)
  );

  for (const variable of explicitVariables) {
    const variableTypeConfigSpec = variable.variableTypeConfigSpec as PipelineConfigJsonSpec;
    const fieldTypeAndMulti = getSchemaFieldTypeAndMultiFromVariableSpec(variableTypeConfigSpec);

    // Return error if variable cannot be mapped to schema field
    if (fieldTypeAndMulti == null) {
      return {
        success: false,
        error: `Analysis template "${analysisTemplate.name}" cannot be used since it has variable "${variable.name}" that cannot be mapped to a schema field.`,
      };
    }

    const {fieldType, isMulti} = fieldTypeAndMulti;
    fields.push({
      name: variable.name,
      systemName: convertApiIdToSchemaFieldSystemName(variable.id),
      fieldType,
      isMulti,
      isRequired: variableTypeConfigSpec.required,
      variableId: variable.id,
    });
  }

  return {success: true, fields};
}

/**
 * Filters fields by persisted analysis template variable mapping (schema_field_api_id -> variable_id).
 * Used when displaying a persisted analysis template.
 *
 * @param allFields - All schema fields
 * @param analysisTemplateVariableMapping - Mapping of schema field API IDs to analysis variable IDs
 * @returns Object containing run fields and template fields
 */
export function filterFieldsByPersistedMapping<T extends {apiId?: string}>(
  allFields: Array<T>,
  analysisTemplateVariableMapping: Record<string, string> | null
): {
  runFields: Array<T>;
  templateFields: Array<T>;
} {
  if (!analysisTemplateVariableMapping || Object.keys(analysisTemplateVariableMapping).length === 0) {
    return {runFields: allFields, templateFields: []};
  }

  const templateFields: Array<T> = [];
  const runFields: Array<T> = [];

  for (const field of allFields) {
    if (field.apiId && analysisTemplateVariableMapping[field.apiId] != null) {
      templateFields.push(field);
    } else {
      runFields.push(field);
    }
  }

  return {runFields, templateFields};
}

/**
 * Filters out draft template fields from all fields by system name.
 * Used when previewing a new analysis template.
 *
 * @param allFields - All schema fields
 * @param draftTemplateFields - Draft template fields to filter out
 * @returns Object containing run fields and template fields
 */
export function filterFieldsByDraftTemplateFields<T extends {systemName: string}>(
  allFields: Array<T>,
  draftTemplateFields: Array<T> | null
): {
  runFields: Array<T>;
  templateFields: Array<T>;
} {
  if (!draftTemplateFields || draftTemplateFields.length === 0) {
    return {runFields: allFields, templateFields: []};
  }

  const draftSystemNames = new Set(draftTemplateFields.map((f) => f.systemName));
  const runFields = allFields.filter((f) => !draftSystemNames.has(f.systemName));

  return {runFields, templateFields: draftTemplateFields};
}

/**
 * Main filtering function that handles both persisted and draft template scenarios.
 * Determines which filtering strategy to use based on whether showing persisted or draft template.
 *
 * @param allFields - All schema fields
 * @param effectiveAnalysisTemplateId - Currently selected template ID (can be draft or persisted)
 * @param persistedAnalysisTemplateId - The persisted template ID from backend
 * @param persistedMapping - Persisted analysis template variable mapping (schema_field_api_id -> variable_id)
 * @param draftTemplateFields - Draft template fields (if previewing a new template)
 * @returns Object containing run fields and template fields
 */
export function filterFieldsForAnalysisTemplate<T extends {apiId?: string; systemName: string}>(
  allFields: Array<T>,
  effectiveAnalysisTemplateId: string | null,
  persistedAnalysisTemplateId: string | null,
  persistedMapping: Record<string, string> | null,
  draftTemplateFields: Array<T> | null
): {
  runFields: Array<T>;
  templateFields: Array<T>;
} {
  if (!effectiveAnalysisTemplateId) {
    // When no template is selected, filter out any persisted template fields
    if (persistedAnalysisTemplateId && persistedMapping) {
      const fieldsWithoutPersisted = allFields.filter(
        (field) => !field.apiId || persistedMapping[field.apiId] == null
      );
      return {runFields: fieldsWithoutPersisted, templateFields: []};
    }
    return {runFields: allFields, templateFields: []};
  }

  if (effectiveAnalysisTemplateId === persistedAnalysisTemplateId) {
    return filterFieldsByPersistedMapping(allFields, persistedMapping);
  }

  // Showing draft template - filter out persisted fields first, then apply draft filtering
  const fieldsWithoutPersisted = persistedMapping
    ? allFields.filter((field) => !field.apiId || persistedMapping[field.apiId] == null)
    : allFields;
  return filterFieldsByDraftTemplateFields(fieldsWithoutPersisted, draftTemplateFields);
}
